import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import MaintenanceTaskDetail from './MaintenanceTaskDetail';
import MaintenanceTaskForm from './MaintenanceTaskForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Calendar,
  Clock,
  Repeat,
  Plus,
  Settings,
  Building2,
  FileText,
  Printer,
  Trash2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

function MaintenanceWorkOrders({ user }) {
  const { t } = useLanguage();
  const [activeView, setActiveView] = useState('daily');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  // Initialize maintenance tasks from localStorage or empty arrays
  const [sampleMaintenanceTasks, setSampleMaintenanceTasks] = useState(() => {
    try {
      const saved = localStorage.getItem('equiptrack:maintenanceTasks');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          daily: parsed.daily || [],
          weekly: parsed.weekly || [],
          monthly: parsed.monthly || [],
          yearly: parsed.yearly || []
        };
      }
    } catch (error) {
      console.error('Failed to load maintenance tasks from localStorage:', error);
    }
    return {
      daily: [],
      weekly: [],
      monthly: [],
      yearly: []
    };
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch departments and machines on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API}/api/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setDepartments(data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    const fetchMachines = async () => {
      try {
        const response = await fetch(`${API}/api/machines`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setMachines(data);
        }
      } catch (error) {
        console.error('Error fetching machines:', error);
      }
    };

    fetchDepartments();
    fetchMachines();
  }, [API]);

  // Migrate old task data to include department/machine names
  const migrateTaskData = (tasks, departments, machines) => {
    return tasks.map(task => {
      // If task already has names, don't change it
      if (task.departmentName && task.machineName) {
        return task;
      }
      
      // Find names for IDs
      const dept = departments.find(d => d.id === task.department);
      const machine = machines.find(m => m.id === task.machine);
      
      return {
        ...task,
        departmentName: dept?.name || task.department || 'N/A',
        machineName: machine?.name || task.machine || 'N/A'
      };
    });
  };

  // Migrate task data when departments and machines are loaded
  useEffect(() => {
    if (departments.length > 0 && machines.length > 0) {
      const migratedTasks = {
        daily: migrateTaskData(sampleMaintenanceTasks.daily || [], departments, machines),
        weekly: migrateTaskData(sampleMaintenanceTasks.weekly || [], departments, machines),
        monthly: migrateTaskData(sampleMaintenanceTasks.monthly || [], departments, machines),
        yearly: migrateTaskData(sampleMaintenanceTasks.yearly || [], departments, machines)
      };
      
      // Update state with migrated tasks
      setSampleMaintenanceTasks(migratedTasks);
    }
  }, [departments, machines]); // Only run when departments/machines change

  // Save maintenance tasks to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('equiptrack:maintenanceTasks', JSON.stringify(sampleMaintenanceTasks));
      // DON'T dispatch event here - it causes infinite loop!
      // Events are dispatched explicitly by create/edit/delete functions
    } catch (error) {
      console.error('Failed to save maintenance tasks to localStorage:', error);
    }
  }, [sampleMaintenanceTasks]);

  const maintenanceTypes = [
    {
      id: 'daily',
      title: t('dailyMaintenance'),
      icon: Clock,
      color: 'bg-blue-500',
      description: t('dailyMaintenanceDesc'),
      count: sampleMaintenanceTasks.daily?.length || 0,
      bgColor: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'weekly',
      title: t('weeklyMaintenance'),
      icon: Calendar,
      color: 'bg-green-500',
      description: t('weeklyMaintenanceDesc'),
      count: sampleMaintenanceTasks.weekly?.length || 0,
      bgColor: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'monthly',
      title: t('monthlyMaintenance'),
      icon: Repeat,
      color: 'bg-purple-500',
      description: t('monthlyMaintenanceDesc'),
      count: sampleMaintenanceTasks.monthly?.length || 0,
      bgColor: 'from-purple-50 to-indigo-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'yearly',
      title: t('yearlyMaintenance'),
      icon: Calendar,
      color: 'bg-orange-500',
      description: 'Annual maintenance schedules',
      count: sampleMaintenanceTasks.yearly?.length || 0,
      bgColor: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200'
    }
  ];

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = (updatedTask) => {
    if (!updatedTask) {
      // Task was deleted, just close modal and reload
      setSelectedTask(null);
      fetchWorkOrders();
      return;
    }
    
    console.log('Maintenance task updated via API:', updatedTask);
    
    // Update the task in the list
    setWorkOrders(prevOrders =>
      prevOrders.map(task =>
        task.id === updatedTask.id ? updatedTask : task
      )
    );
    
    // Update selectedTask if it's currently open
    if (selectedTask && selectedTask.id === updatedTask.id) {
      setSelectedTask(updatedTask);
    }
    
    // Dispatch event for calendar update
    window.dispatchEvent(new CustomEvent('workOrderUpdated', {
      detail: updatedTask
    }));
  };

  const handleTaskCreate = (newTask) => {
    // Task is already created in backend by MaintenanceTaskForm
    // Just reload the work orders and close the form
    console.log('Maintenance task created via API:', newTask);
    
    // Reload work orders to show the new task
    fetchWorkOrders();
    
    // Dispatch event for calendar update
    window.dispatchEvent(new CustomEvent('workOrderUpdated'));
    
    setShowTaskForm(false);
  };

  const handleTaskDelete = (taskId) => {
    if (window.confirm('Are you sure you want to delete this maintenance task?')) {
      setSampleMaintenanceTasks(prev => ({
        ...prev,
        [activeView]: prev[activeView].filter(task => task.id !== taskId)
      }));
    }
  };

  const handleDeleteTask = async (e, taskId, frequency) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this maintenance task?')) {
      try {
        // Delete from backend API
        await fetch(`${API}/api/work-orders/${taskId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Close modal if the deleted task was selected
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(null);
        }
        
        // Reload work orders
        fetchWorkOrders();
        
        // Dispatch event for calendar update
        window.dispatchEvent(new CustomEvent('workOrderUpdated'));
        
        toast.success('Task deleted successfully');
      } catch (error) {
        console.error('Failed to delete task:', error);
        toast.error('Failed to delete task');
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency?.toLowerCase()) {
      case 'daily':
        return 'bg-blue-100 text-blue-800';
      case 'weekly':
        return 'bg-green-100 text-green-800';
      case 'monthly':
        return 'bg-purple-100 text-purple-800';
      case 'yearly':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fetch PM work orders from API
  const fetchWorkOrders = async () => {
    try {
      const response = await fetch(`${API}/api/work-orders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter for PM (Preventive Maintenance) type work orders
        // Exclude completed work orders as they should be in history
        const pmWorkOrders = data
          .filter(wo => wo.type === 'PM' && wo.status !== 'Completed')
          .map(wo => ({
            ...wo,
            // Add camelCase aliases for display
            departmentName: wo.department_name,
            machineName: wo.machine_name,
            assigneeName: wo.assignee_name || wo.assignee
          }));
        console.log('Loaded PM work orders from API:', pmWorkOrders.length);
        
        // Group by frequency
        const grouped = {
          daily: pmWorkOrders.filter(wo => wo.frequency === 'Daily'),
          weekly: pmWorkOrders.filter(wo => wo.frequency === 'Weekly'),
          monthly: pmWorkOrders.filter(wo => wo.frequency === 'Monthly'),
          yearly: pmWorkOrders.filter(wo => wo.frequency === 'Yearly')
        };
        
        console.log('Grouped tasks:', {
          daily: grouped.daily.length,
          weekly: grouped.weekly.length,
          monthly: grouped.monthly.length,
          yearly: grouped.yearly.length
        });
        
        setSampleMaintenanceTasks(grouped);
        setWorkOrders(data.filter(wo => wo.status !== 'Completed'));  // Also filter workOrders state
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  React.useEffect(() => {
    fetchWorkOrders();

    // Listen for work order updates from other pages
    const handleWorkOrderUpdate = () => {
      console.log('Received workOrderUpdated event, reloading...');
      fetchWorkOrders();
    };

    window.addEventListener('workOrderUpdated', handleWorkOrderUpdate);
    
    // Cleanup listener
    return () => {
      window.removeEventListener('workOrderUpdated', handleWorkOrderUpdate);
    };
  }, [API]);

  // Get all maintenance tasks from all frequencies combined
  const allMaintenanceTasks = [
    ...(sampleMaintenanceTasks.daily || []).map(task => ({ ...task, frequency: 'Daily' })),
    ...(sampleMaintenanceTasks.weekly || []).map(task => ({ ...task, frequency: 'Weekly' })),
    ...(sampleMaintenanceTasks.monthly || []).map(task => ({ ...task, frequency: 'Monthly' })),
    ...(sampleMaintenanceTasks.yearly || []).map(task => ({ ...task, frequency: 'Yearly' }))
  ];

  return (
    <div className="space-y-6" data-testid="maintenance-work-orders">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Settings className="w-6 h-6 mr-2 text-green-600" />
              {t('maintenanceWorkOrders')}
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your maintenance schedules - {allMaintenanceTasks.length} {t('totalTasks')}
            </p>
          </div>
          <Button
            onClick={() => setShowTaskForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addTask')}
          </Button>
        </div>
      </div>

      {/* Maintenance Type Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {maintenanceTypes.map((type) => {
          const Icon = type.icon;
          
          return (
            <Card key={type.id} className={`border-2 ${type.borderColor} ${type.bgColor}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`${type.color} p-2 rounded-lg`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{type.title}</h3>
                      <p className="text-xs text-gray-600">{type.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-gray-900">{type.count}</div>
                    <div className="text-xs text-gray-500">Tasks</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Render task sections by frequency */}
      {['Daily', 'Weekly', 'Monthly', 'Yearly'].map(freq => {
        const tasksForFreq = allMaintenanceTasks.filter(t => t.frequency === freq);
        if (tasksForFreq.length === 0) return null;
        
        const colors = {
          'Daily': { bg: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' },
          'Weekly': { bg: 'bg-green-500', badge: 'bg-green-100 text-green-700' },
          'Monthly': { bg: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700' },
          'Yearly': { bg: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' }
        };
        
        return (
          <div key={freq} className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${colors[freq].bg}`}></div>
                <h3 className="text-lg font-semibold text-gray-900">{freq} Maintenance</h3>
                <Badge variant="secondary" className={colors[freq].badge}>
                  {tasksForFreq.length}
                </Badge>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasksForFreq.map((task) => (
                <Card
                  key={task.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-200 bg-white border border-gray-200"
                  onClick={() => setSelectedTask(task)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-2">{task.title}</h3>
                      </div>
                      <button
                        onClick={(e) => handleDeleteTask(e, task.id, task.frequency)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete task template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{task.departmentName || task.department}</span>
                      </div>
                      <div className="flex items-center">
                        <Settings className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{task.machineName || task.machine}</span>
                      </div>
                      {(task.assignee_names && task.assignee_names.length > 0) ? (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{task.assignee_names.join(', ')}</span>
                        </div>
                      ) : (task.assignee_name || task.assignee) && (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{task.assignee_name || task.assignee}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between gap-2">
                      <Badge className={`text-xs ${getFrequencyColor(task.frequency)}`}>
                        {task.frequency}
                      </Badge>
                      <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
        );
      })}

      {/* Maintenance Task Detail Modal */}
      {selectedTask && (
        <MaintenanceTaskDetail
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          user={user}
        />
      )}

      {/* Maintenance Task Form Modal */}
      {showTaskForm && (
        <MaintenanceTaskForm
          onClose={() => setShowTaskForm(false)}
          onCreate={handleTaskCreate}
          user={user}
        />
      )}
    </div>
  );
}

export default MaintenanceWorkOrders;